import React, { useState, useEffect } from "react";
import { logEvent } from "@src/utils/logClient";
import Translate from "./translation/Translate";
import { Button, DetailAction, DetailText, CloseIcon, theme } from "./nessie-web";
import styled from "@emotion/styled";
import { LocalStorageWrapper } from "@src/utils/localStorage";
import { surveyKey, timeBeforeShowingSurvayAgain } from "@src/utils/survey";
import { useFeatureFlag } from "@src/utils/useFeatureFlag";
import { ExternalSwitches } from "@src/utils/experiments/constants";
import getLocale from "@src/utils/getLocale";

type UserSurveyProps = {
  performanceCookieConsent: boolean;
};
type AnswerId = {
  id: string;
  translation: string;
};

const Box = styled.div`
  background: white;
  padding: ${theme.space.dt_m}px;
  max-width: 400px;
  border-radius: ${theme.radii.dt_radius_s};
  position: fixed;
  bottom: ${theme.space.dt_m}px;
  right: ${theme.space.dt_m}px;
  border: ${theme.borders.dt_border_card};
  shadow: ${theme.shadows.dt_shadow_shadezies};
`;
const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: transparent;
  outline: 0;
  border: 0;
  cursor: pointer;
`;
const UserSurvey: React.FC<UserSurveyProps> = ({ performanceCookieConsent }) => {
  const [showSurvey, setShowSurvey] = useState(false);
  const [userAnswer, setUserAnswer] = useState<string | null>(null);
  const [showThanks, setShowThanks] = useState(false);

  const answerIds: Record<string, AnswerId> = {
    answer1: { id: "teacher-info", translation: "directus.homepage_copy_experiments.teacher_info" },
    answer2: { id: "school-admin-info", translation: "directus.homepage_copy_experiments.school_admin_info" },
    answer3: { id: "district-admin-info", translation: "directus.homepage_copy_experiments.district_admin_info" },
    answer4: { id: "family-member-info", translation: "directus.homepage_copy_experiments.family_member_info" },
    answer5: { id: "student-info", translation: "directus.homepage_copy_experiments.student_info" },
    answer6: { id: "returning-user", translation: "directus.homepage_copy_experiments.returning_user" },
    answer7: { id: "other", translation: "directus.homepage_copy_experiments.other" },
  };

  // gets user locale from pathname
  function getLocaleFromPath(pathname) {
    const regex = /^\/([a-z]{2}-[a-z]{2})/;
    const match = pathname.match(regex);
    return match ? match[1] : "en-us";
  }

  useEffect(() => {
    const surveyData = JSON.parse(LocalStorageWrapper.getItem(surveyKey) || "{}");
    const wasDismissedRecently =
      surveyData.dismissedAt && Date.now() - surveyData.dismissedAt < timeBeforeShowingSurvayAgain;
    if (!wasDismissedRecently) {
      setShowSurvey(true);
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserAnswer(e.target.value);
  };
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (userAnswer) {
      logEvent({
        eventName: "external.userSurvey",
        eventValue: userAnswer,
        metadata: {
          location: getLocaleFromPath(window.location.pathname),
        },
      });
      LocalStorageWrapper.setItem(surveyKey, JSON.stringify({ dismissedAt: Date.now() }));
      setUserAnswer(null);
      setShowThanks(true);
      setTimeout(() => {
        setShowSurvey(false);
        setShowThanks(false);
      }, 3000);
    }
  };

  const handleDismiss = () => {
    LocalStorageWrapper.setItem(surveyKey, JSON.stringify({ dismissedAt: Date.now() }));
    setShowSurvey(false);
  };

  const isSurveyFSon = useFeatureFlag(ExternalSwitches.WEB_EXTERNAL_USER_SURVEY).actualValue === "on";
  const isAssistantFSon = useFeatureFlag(ExternalSwitches.WEB_EXTERNAL_AI_SUPPORT).actualValue === "test";

  return (
    <>
      {isSurveyFSon && !isAssistantFSon && performanceCookieConsent && showSurvey && !showThanks && (
        <Box id="user-survey">
          <CloseButton onClick={handleDismiss} style={{}}>
            <CloseIcon />
          </CloseButton>
          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <DetailAction>
              <Translate path="directus.homepage_copy_experiments.Question" />
            </DetailAction>
            {Object.entries(answerIds).map(([key, { id, translation }]) => (
              <div key={id} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                <input
                  type="radio"
                  id={id}
                  name="survey"
                  value={id}
                  required
                  onChange={handleChange}
                  style={{ marginTop: 3 }}
                />
                <DetailText as="label" htmlFor={id}>
                  <Translate path={translation} />
                </DetailText>
              </div>
            ))}
            <Button type="submit" size="s" style={{ marginTop: 8 }}>
              <Translate path="directus.homepage_copy_experiments.survey_cta" />
            </Button>
          </form>
        </Box>
      )}
      {showThanks && (
        <Box id="thanks">
          <DetailAction>
            <Translate path="directus.homepage_copy_experiments.survey_feedback" />
          </DetailAction>
        </Box>
      )}
    </>
  );
};

export default UserSurvey;
